/**
 * src/shared/lib/analytics.ts
 *
 * Cookieless, PII-free, non-blocking client analytics (FUNNEL-01).
 * track() posts to /api/events via navigator.sendBeacon (preferred) or
 * fetch({keepalive:true}) fallback — fire-and-forget, all errors swallowed.
 *
 * Identity is derived server-side from the session cookie (httpOnly, same-origin).
 * No client-side user identifier is embedded in props — T-18-10 mitigation.
 *
 * Analogous module conventions: src/shared/lib/changeQueue.ts
 * (graceful degradation on failure, never throw, never block caller).
 */

// IN-02 fix: import the canonical allowlist from schemas.ts (single source of truth).
// Previously a hand-rolled Set(['app_open', 'activation']) that could silently drift
// from the server-side CLIENT_EVENT_ALLOWLIST. Any new event added to schemas.ts is
// now automatically reflected here without a manual update.
import { CLIENT_EVENT_ALLOWLIST } from '@/shared/validation/schemas';

const ANALYTICS_ENDPOINT = '/api/events';

/**
 * Client event allowlist — derived from CLIENT_EVENT_ALLOWLIST in schemas.ts
 * so that client and server allowlists cannot drift (IN-02 fix).
 */
const ALLOWED_CLIENT_EVENTS = new Set<string>(CLIENT_EVENT_ALLOWLIST);

/**
 * Post a fire-and-forget analytics event to /api/events.
 *
 * - Uses navigator.sendBeacon with a Blob(application/json) so Hono's zValidator
 *   receives correct Content-Type (Pitfall 4 — raw string → text/plain → 400).
 * - Falls back to fetch({keepalive:true}) when sendBeacon is absent or returns false.
 * - Swallows ALL errors — analytics must never surface to the user or block render.
 * - Sends no client-side userId; server derives identity from the session cookie.
 *
 * @param event - Event name (must be in ALLOWED_CLIENT_EVENTS)
 * @param props - Optional PII-free primitive props (room_count, total_gt_zero, etc.)
 */
export function track(event: string, props?: Record<string, unknown>): void {
  // Guard: skip if not in a browser context (SSR / test environments without navigator)
  if (typeof navigator === 'undefined') return;

  // Guard: only allowed client events — avoids accidental server-only event emission
  if (!ALLOWED_CLIENT_EVENTS.has(event)) return;

  try {
    const body = JSON.stringify({ event, props: props ?? {} });
    const blob = new Blob([body], { type: 'application/json' });

    // sendBeacon: fire-and-forget, survives page unload, returns bool (not a Promise)
    const queued = navigator.sendBeacon?.(ANALYTICS_ENDPOINT, blob);

    // IN-01 fix: merge the two identical fallback branches (queued===false and
    // queued===undefined) into one condition — both cases use the same fetch options.
    if (queued === false || queued === undefined) {
      // sendBeacon was rejected (quota/rate-limit) or unavailable (old browser/test stub).
      // Fall back to keepalive fetch — also survives navigation/unload.
      fetch(ANALYTICS_ENDPOINT, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        credentials: 'include',
      }).catch(() => {
        // Swallow: analytics must never surface errors to the user (T-18-12)
      });
    }
  } catch {
    // Swallow ALL errors — track() must never throw to the caller (T-18-12)
  }
}
